import { useAlert } from '@/contexts/Toast'
import { useRoleCategory } from '@/hooks/useRoleCategory'
import { getErrorMessage, getStatusCode, onAccountRevoked } from '@/lib/axios'
import { auth } from '@/lib/firebase'
import {
  CrmPermissionCode,
  PermissionAction,
  Profile,
  UserRole,
} from '@/services/authentication/model'
import { LOYALTY_USE_MOCK } from '@/services/loyalty'
import { WORKOUT_USE_MOCK } from '@/services/workout'
import {
  set80mmReceiptEnabled,
  setESignatureEnabled,
  setCreditFeatureEnabled,
} from '@/utils/helper'
import {
  confirmPasswordReset,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
  User,
  verifyPasswordResetCode,
} from 'firebase/auth'
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useClient } from './Services'

/**
 * QTY-2042: the signed-in staff account is In-Active. Thrown by the bootstrap
 * so the sign-out happens where `logout` is in scope, and so `refreshProfile`
 * — which otherwise swallows failures to keep the last-good session — can tell
 * this apart from a transient error.
 */
class AccountInactiveError extends Error {
  constructor() {
    super('ACCOUNT_INACTIVE')
    this.name = 'AccountInactiveError'
  }
}

interface AuthContextType {
  isCreditFeatureEnabled: boolean
  isLoyaltyEnabled: boolean
  /** Alias of `isLoyaltyEnabled` — matches the `isXFeatureEnabled` naming
   *  convention used by the rest of the auth context. Both keys return the
   *  same boolean; new code should prefer this name. */
  isLoyaltyFeatureEnabled: boolean
  isESignatureEnabled: boolean
  is80mmReceiptEnabled: boolean
  isAccessControlEnabled: boolean
  isRfidCheckinEnabled: boolean
  isAiCoachingEnabled: boolean
  isLactateTestEnabled: boolean
  isWorkoutBuilderEnabled: boolean
  isDownloadCsvReportAvailable: boolean
  isLogin: boolean
  isLoading: boolean
  user: User | null
  profile: Profile | null
  isSaleRole: boolean
  isTrainerRole: boolean
  isAquaticTrainerRole: boolean
  roleAndPermissions: UserRole | null
  tenantId: string | null
  clubName: string | null
  clubLogoUrl: string | null
  login: (email: string, password: string) => Promise<void>
  hasPermission: (featureName: CrmPermissionCode, action: PermissionAction) => boolean
  resetPassword: (email: string) => Promise<void>
  handleResetPassword: (url: URL, newPassword: string) => Promise<void>
  logout: () => Promise<void>
  getToken: () => Promise<string | null>
  setTenantId: (tenantId: string | null) => void
  refreshProfile: () => Promise<void>
  /** True when the initial tenant bootstrap (profile + club-info) failed with a
   *  transient error (5xx/network) and the session was kept alive instead of
   *  bouncing to login. Consumers can show a retryable error state. */
  hasBootstrapError: boolean
  /** Re-run the tenant bootstrap after a transient failure. */
  retryBootstrap: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const client = useClient()
  const { alert } = useAlert()
  const { t } = useTranslation()
  const [user, setUser] = useState<User | null>(null)
  const [profile, setProfile] = useState<Profile | null>(null)
  const [roleAndPermissions, setRoleAndPermissions] = useState<UserRole | null>(null)
  const [isCreditFeatureEnabledState, setIsCreditFeatureEnabledState] = useState(false)
  const [isLoyaltyEnabledState, setIsLoyaltyEnabledState] = useState(false)
  const [isESignatureEnabledState, setIsESignatureEnabledState] = useState(false)
  const [is80mmReceiptEnabledState, setIs80mmReceiptEnabledState] = useState(false)
  const [isAccessControlEnabledState, setIsAccessControlEnabledState] = useState(false)
  const [isRfidCheckinEnabledState, setIsRfidCheckinEnabledState] = useState(false)
  const [isAiCoachingEnabledState, setIsAiCoachingEnabledState] = useState(false)
  const [isLactateTestEnabledState, setIsLactateTestEnabledState] = useState(false)
  const [isWorkoutBuilderEnabledState, setIsWorkoutBuilderEnabledState] = useState(false)
  const [isDownloadCsvReportAvailableState, setIsDownloadCsvReportAvailableState] = useState(false)
  const [clubName, setClubName] = useState<string | null>(null)
  const [clubLogoUrl, setClubLogoUrl] = useState<string | null>(null)
  const [isLogin, setIsLogin] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  // Bootstrap = the tenant-scoped data load (branches + profile + club-info)
  // that runs after login/tenant selection. hasBootstrapError is set only
  // after retries are exhausted on a transient failure, so ProtectedRoute can
  // show a recoverable retry screen instead of an infinite spinner.
  // bootstrapNonce is bumped by retryBootstrap() to re-trigger the bootstrap
  // effect on demand, without touching user/tenant identity.
  const [hasBootstrapError, setHasBootstrapError] = useState(false)
  const [bootstrapNonce, setBootstrapNonce] = useState(0)
  const [tenantId, setTenantIdState] = useState<string | null>(
    () => localStorage.getItem('tenantId') || null
  )

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      // Preserve the existing User reference across token auto-refreshes
      // (uid unchanged). Firebase fires this callback whenever the ID token
      // is refreshed (~hourly); without this guard, every refresh produced a
      // new ref and re-triggered the loadAuthState effect below, kicking off
      // /auth/me + /club-info again and stalling the app during backend cold
      // starts.
      setUser((prev) => {
        if (prev?.uid === firebaseUser?.uid) return prev
        return firebaseUser
      })
      setIsLogin(!!firebaseUser)

      setIsLoading(false)
    })

    return () => {
      setIsLoading(true)
      unsubscribe()
    }
  }, [])

  const login = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password)
    setIsLoading(true)
  }

  const setTenantId = (nextTenantId: string | null) => {
    if (nextTenantId) {
      localStorage.setItem('tenantId', nextTenantId)
    } else {
      localStorage.removeItem('tenantId')
    }
    // Don't assume a branch here — the user's accessible branches aren't known
    // yet. Hardcoding branch 1 broke login for staff without branch-1 access
    // (QTY-1855); loadAuthState resolves a valid branch before any
    // branch-scoped request.
    localStorage.removeItem('currentBranch')
    setTenantIdState(nextTenantId)
  }

  const logout = async () => {
    // Clear the branch selection up-front so a stale/invalid branch never
    // survives a logout — including the "wrong branch" force-logout in
    // loadAuthState — even if signOut() below throws before the reset runs.
    // Next login re-derives a valid branch in ensureValidCurrentBranch.
    localStorage.removeItem('currentBranch')
    await signOut(auth)
    setUser(null)
    setProfile(null)
    setRoleAndPermissions(null)
    setIsCreditFeatureEnabledState(false)
    setIsLoyaltyEnabledState(false)
    setIsESignatureEnabledState(false)
    setIs80mmReceiptEnabledState(false)
    setIsAccessControlEnabledState(false)
    setIsRfidCheckinEnabledState(false)
    setIsAiCoachingEnabledState(false)
    setIsLactateTestEnabledState(false)
    setIsDownloadCsvReportAvailableState(false)
    setClubName(null)
    setClubLogoUrl(null)
    setCreditFeatureEnabled(false)
    setESignatureEnabled(false)
    set80mmReceiptEnabled(false)
    setIsLogin(false)
    setTenantId(null)
  }

  const resetPassword = async (email: string) => {
    await client!.authentication.forgotPassword(email)
  }

  const hasPermission = useCallback(
    (featureName: CrmPermissionCode, action: PermissionAction): boolean => {
      if (!roleAndPermissions) return false
      const permission = roleAndPermissions.permissions.find(
        (p) => p.code === featureName && p.action === action
      )
      return !!permission
    },
    [roleAndPermissions]
  )

  const { roleIdList: salesOwnerRoleIds } = useRoleCategory('sales-owner')
  const { roleIdList: instructorRoleIds } = useRoleCategory('instructor')
  const { roleIdList: aquaticRoleIds } = useRoleCategory('aquatic')

  const isSaleRole = useMemo(() => {
    if (!profile) return false
    return salesOwnerRoleIds.includes(+profile.roleId) || !!profile.presetCodes?.includes('sale')
  }, [profile, salesOwnerRoleIds])

  const isTrainerRole = useMemo(() => {
    if (!profile) return false
    return instructorRoleIds.includes(+profile.roleId)
  }, [profile, instructorRoleIds])

  const isAquaticTrainerRole = useMemo(() => {
    if (!profile) return false
    return aquaticRoleIds.includes(+profile.roleId)
  }, [profile, aquaticRoleIds])

  // Pick a branch the signed-in user can actually access before issuing any
  // branch-scoped request. GET /club-info is @RequireBranch and 403s on a
  // branch-id the user has no access to (QTY-1855); GET /me/profile/branches is
  // not branch-scoped, so it's safe to call with no branch-id header. Keep a
  // still-valid stored selection, otherwise fall back to the first branch.
  const ensureValidCurrentBranch = useCallback(async () => {
    const stored = localStorage.getItem('currentBranch')
    const storedId = stored && !isNaN(Number(stored)) ? Number(stored) : null
    // The branches request itself skips the branch-id header
    // (x-skip-branch-id), so the stored selection can stay in place while we
    // validate it. Deleting it first opened a window on every reload where
    // parallel queries fired headerless and 400'd on @RequireBranch endpoints.
    const branches = await client!.authentication.getMyBranches()
    if (branches.length === 0) {
      localStorage.removeItem('currentBranch')
      return
    }
    const chosen = branches.some((b) => b.id === storedId) ? storedId! : branches[0].id
    localStorage.setItem('currentBranch', String(chosen))
  }, [client])

  const loadAuthState = useCallback(
    async (isActive?: () => boolean) => {
      await ensureValidCurrentBranch()
      const [authResponse, clubInfoResponse] = await Promise.all([
        client!.authentication.getMyProfile(),
        client!.clubInfo.getClubInfo(),
      ])
      if (isActive && !isActive()) return
      // QTY-2042: a staff member set to In-Active may still hold a valid
      // Firebase session — deactivation is a status flag, and the Firebase
      // account is deliberately left alone so the same person keeps working in
      // other tenants. Bail before any state is published so they never reach
      // the console, instead of waiting for the first API call to 403.
      if (authResponse.profile?.accountStatus === 'INACTIVE') {
        throw new AccountInactiveError()
      }
      setRoleAndPermissions(authResponse.role)
      setProfile(authResponse.profile)
      setClubName(clubInfoResponse.businessInformation?.companyName ?? null)
      setClubLogoUrl(clubInfoResponse.imageUrl)
      setIsCreditFeatureEnabledState(clubInfoResponse.config?.isCreditEnabled ?? false)
      // Loyalty: while the FE runs on the mock (VITE_LOYALTY_USE_MOCK
      // unset/'true') keep the demo UI visible regardless of the club flag.
      // On live, the BE /club-info flag is authoritative — it mirrors the
      // BE's LoyaltyFeatureGuard, which 403s /loyalty/* when disabled.
      setIsLoyaltyEnabledState(
        LOYALTY_USE_MOCK || (clubInfoResponse.config?.isLoyaltyEnabled ?? false)
      )
      setIsESignatureEnabledState(clubInfoResponse.config?.isESignatureEnabled ?? false)
      setIs80mmReceiptEnabledState(clubInfoResponse.config?.is80mmReceipt ?? false)
      setIsAccessControlEnabledState(clubInfoResponse.config?.isAccessControlEnabled ?? false)
      setIsRfidCheckinEnabledState(clubInfoResponse.config?.isRfidCheckinEnabled ?? false)
      setIsAiCoachingEnabledState(
        clubInfoResponse.config?.isAiCoachingEnabled ??
          clubInfoResponse.config?.isHumanArchitectEnabled ??
          false
      )
      setIsLactateTestEnabledState(clubInfoResponse.config?.isLactateTestEnabled ?? false)
      // Workout builder: same policy as loyalty — visible while the FE mock
      // runs; on live the club-config flag is authoritative.
      setIsWorkoutBuilderEnabledState(
        WORKOUT_USE_MOCK || (clubInfoResponse.config?.isWorkoutBuilderEnabled ?? false)
      )
      setIsDownloadCsvReportAvailableState(clubInfoResponse.isDownloadCsvReportAvailable ?? false)
      setCreditFeatureEnabled(clubInfoResponse.config?.isCreditEnabled ?? false)
      setESignatureEnabled(clubInfoResponse.config?.isESignatureEnabled ?? false)
      set80mmReceiptEnabled(clubInfoResponse.config?.is80mmReceipt ?? false)
    },
    [client, ensureValidCurrentBranch]
  )

  const retryBootstrap = useCallback(() => {
    setHasBootstrapError(false)
    setBootstrapNonce((n) => n + 1)
  }, [])

  const refreshProfile = useCallback(
    async () => {
      try {
        await loadAuthState()
      } catch (error) {
        // QTY-2042: deactivated while the session was open — this is the one
        // refresh failure that must end the session rather than be swallowed.
        if (error instanceof AccountInactiveError) {
          void logout()
          alert(t('staff-status.no-access'), 'error')
          return
        }
        // Silent: keep last-good auth state. A transient refresh failure must
        // not log the user out — the existing session remains valid.
      }
    },
    // `logout` and `alert` are recreated every render but only ever read
    // here, so they stay out of the list to keep this callback stable.
    [loadAuthState, t]
  )

  // QTY-2042: the backend rejects every request from an In-Active account, so
  // a staff member deactivated mid-session is signed out by the next query any
  // open tab fires — no polling, and it covers every tab and device.
  useEffect(() => {
    return onAccountRevoked(() => {
      void logout()
      alert(t('staff-status.no-access'), 'error')
    })
    // Same reasoning as above: `logout`/`alert` are read, never depended on.
  }, [t])

  useEffect(() => {
    if (!user) {
      // Also drop any stale bootstrap error so it can't flash the retry
      // screen on the next login before the bootstrap effect re-runs.
      setHasBootstrapError(false)
      setProfile(null)
      setRoleAndPermissions(null)
      setIsCreditFeatureEnabledState(false)
      setIsLoyaltyEnabledState(false)
      setIsESignatureEnabledState(false)
      setIs80mmReceiptEnabledState(false)
      setIsAccessControlEnabledState(false)
      setIsRfidCheckinEnabledState(false)
      setIsAiCoachingEnabledState(false)
      setIsLactateTestEnabledState(false)
      setIsDownloadCsvReportAvailableState(false)
      setClubName(null)
      setClubLogoUrl(null)
      setCreditFeatureEnabled(false)
      setESignatureEnabled(false)
      set80mmReceiptEnabled(false)
      return
    }

    if (!tenantId) {
      setHasBootstrapError(false)
      setProfile(null)
      setRoleAndPermissions(null)
      setIsCreditFeatureEnabledState(false)
      setIsLoyaltyEnabledState(false)
      setIsESignatureEnabledState(false)
      setIs80mmReceiptEnabledState(false)
      setIsAccessControlEnabledState(false)
      setIsRfidCheckinEnabledState(false)
      setIsAiCoachingEnabledState(false)
      setIsLactateTestEnabledState(false)
      setIsDownloadCsvReportAvailableState(false)
      setClubName(null)
      setClubLogoUrl(null)
      setCreditFeatureEnabled(false)
      setESignatureEnabled(false)
      set80mmReceiptEnabled(false)
      return
    }

    let isActive = true
    let attempt = 0
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    const MAX_BOOTSTRAP_RETRIES = 4

    setHasBootstrapError(false)

    const runBootstrap = () => {
      loadAuthState(() => isActive).catch((error) => {
        if (!isActive) return
        // QTY-2042: deactivated staff — never retry, never surface a technical
        // message. Sign out with the "no access" copy the ticket asks for.
        if (error instanceof AccountInactiveError) {
          void logout()
          alert(t('staff-status.no-access'), 'error')
          return
        }
        // Genuine auth failures (401 unauthenticated / 403 forbidden) mean the
        // session is invalid — sign the user out.
        const status = getStatusCode(error)
        if (status === 401 || status === 403) {
          void logout()
          alert(getErrorMessage(error), 'error')
          return
        }
        // Transient server/network errors — a 500, a Cloud Run cold start, or a
        // dropped connection on /me/profile, /club-info, or /me/profile/branches
        // — must NOT sign the user out. Retry with exponential backoff so a cold
        // start self-heals; only after exhausting retries surface a recoverable
        // error state (ProtectedRoute renders a retry screen) instead of
        // stranding the user on an infinite loading spinner.
        if (attempt < MAX_BOOTSTRAP_RETRIES) {
          const delay = Math.min(1000 * 2 ** attempt, 8000)
          attempt += 1
          retryTimer = setTimeout(runBootstrap, delay)
          return
        }
        setHasBootstrapError(true)
        alert(getErrorMessage(error), 'error')
      })
    }

    runBootstrap()

    return () => {
      isActive = false
      if (retryTimer) clearTimeout(retryTimer)
    }
  }, [tenantId, user, bootstrapNonce])

  const handleResetPassword = async (url: URL, newPassword: string) => {
    const params = new URLSearchParams(url.search)
    const oobCode = params.get('oobCode')
    const mode = params.get('mode')

    if (mode !== 'resetPassword') {
      throw new Error('auth/invalid-action-code')
    }

    if (!oobCode) {
      throw new Error('auth/invalid-oob-code')
    }

    await verifyPasswordResetCode(auth, oobCode)
    await confirmPasswordReset(auth, oobCode, newPassword)
  }

  const getToken = async () => {
    const currentUser = auth.currentUser
    return currentUser ? await currentUser.getIdToken() : null
  }

  return (
    <AuthContext.Provider
      value={{
        isCreditFeatureEnabled: isCreditFeatureEnabledState,
        isLoyaltyEnabled: isLoyaltyEnabledState,
        isLoyaltyFeatureEnabled: isLoyaltyEnabledState,
        isESignatureEnabled: isESignatureEnabledState,
        is80mmReceiptEnabled: is80mmReceiptEnabledState,
        isAccessControlEnabled: isAccessControlEnabledState,
        isRfidCheckinEnabled: isRfidCheckinEnabledState,
        isAiCoachingEnabled: isAiCoachingEnabledState,
        isLactateTestEnabled: isLactateTestEnabledState,
        isWorkoutBuilderEnabled: isWorkoutBuilderEnabledState,
        isDownloadCsvReportAvailable: isDownloadCsvReportAvailableState,
        isLoading,
        isLogin,
        user,
        roleAndPermissions,
        hasPermission,
        profile,
        login,
        logout,
        resetPassword,
        handleResetPassword,
        getToken,
        isSaleRole,
        isTrainerRole,
        isAquaticTrainerRole,
        tenantId,
        setTenantId,
        refreshProfile,
        hasBootstrapError,
        retryBootstrap,
        clubName,
        clubLogoUrl,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within an AuthProvider')
  return context
}
